import { computed, nextTick, onBeforeUnmount, readonly, ref, watch } from 'vue'
import { isProfileRequestCurrent } from './accountProfile'

const PRIVATE_PHOTO = /^\/api\/patient-profile-photos\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i
const MAX_IDLE_PHOTOS = 32
const MAX_IDLE_BYTES = 8 * 1024 * 1024
const MAX_PHOTO_BYTES = 20 * 1024 * 1024
const entries = new Map()
const epoch = ref(0)
const refreshedTokens = new Set()
let lastSession = null
let listenersBound = false

export const isPrivateProfilePhotoUrl = source => typeof source === 'string' && PRIVATE_PHOTO.test(source)

const readSession = () => {
  try {
    const profile = JSON.parse(localStorage.getItem('userData') || 'null')
    return { id: profile?.id || null, token: localStorage.getItem('accessToken') }
  } catch { return { id: null, token: null } }
}

const discard = entry => {
  entry.controller.abort()
  if (entry.url) URL.revokeObjectURL(entry.url)
  entry.url = null
  entry.stale = true
  if (entries.get(entry.source) === entry) entries.delete(entry.source)
}

const synchronizeSession = (normalRefresh = false) => {
  const session = readSession()
  if (lastSession && session.id === lastSession.id && session.token === lastSession.token) return session
  if (normalRefresh && lastSession?.id && session.id === lastSession.id && lastSession.token && session.token) {
    refreshedTokens.add(lastSession.token)
    if (refreshedTokens.size > 8) refreshedTokens.delete(refreshedTokens.values().next().value)
    lastSession = session
    return session
  }
  for (const entry of entries.values()) discard(entry)
  entries.clear()
  refreshedTokens.clear()
  lastSession = session
  epoch.value += 1
  return session
}

const bindSessionListeners = () => {
  if (listenersBound || typeof window === 'undefined') return
  listenersBound = true
  window.addEventListener('yournutri:photo-session-changed', event => synchronizeSession(event.detail?.refresh === true))
  window.addEventListener('yournutri:auth-session-expired', () => synchronizeSession())
  window.addEventListener('storage', event => {
    if (event.key === null || ['accessToken', 'userData'].includes(event.key)) synchronizeSession()
  })
}

export function capturePhotoSession() {
  bindSessionListeners()
  const session = synchronizeSession()
  return { ...session, epoch: epoch.value }
}

export function isPhotoSessionCurrent(snapshot, response) {
  const current = synchronizeSession()
  return Boolean(snapshot?.id && snapshot.token && snapshot.epoch === epoch.value && snapshot.id === current.id
    && (isProfileRequestCurrent(snapshot, response, false) || refreshedTokens.has(snapshot.token)))
}

export function clearProfilePhotos() {
  for (const entry of entries.values()) discard(entry)
  entries.clear()
  refreshedTokens.clear()
  lastSession = readSession()
  epoch.value += 1
}

const trimIdleCache = () => {
  const idle = [...entries.values()].filter(entry => entry.references === 0 && entry.url)
    .sort((left, right) => left.usedAt - right.usedAt)
  let bytes = idle.reduce((total, entry) => total + entry.bytes, 0)
  while (idle.length > MAX_IDLE_PHOTOS || bytes > MAX_IDLE_BYTES) {
    const oldest = idle.shift()
    bytes -= oldest.bytes
    discard(oldest)
  }
}

const publicSource = source => {
  if (typeof source !== 'string' || !source || /^blob:|^data:|^javascript:/i.test(source)) return ''
  if (source.includes('/api/patient-profile-photos')) return ''
  try {
    const url = new URL(source, window.location.origin)
    return ['http:', 'https:'].includes(url.protocol) ? source : ''
  } catch { return '' }
}

export function acquireProfilePhoto(source) {
  const snapshot = capturePhotoSession()
  if (!isPrivateProfilePhotoUrl(source)) {
    return { promise: Promise.resolve(publicSource(source)), release: () => {}, source }
  }
  if (!snapshot.id || !snapshot.token) {
    return { promise: Promise.reject(new Error('Profile photo unavailable.')), release: () => {}, source }
  }
  let entry = entries.get(source)
  if (!entry) {
    entry = { source, snapshot, controller: new AbortController(), references: 0, usedAt: Date.now(), bytes: 0, url: null, stale: false }
    entries.set(source, entry)
    entry.promise = (async () => {
      try {
        const { default: axios } = await import('@axios')
        if (entry.stale || !isPhotoSessionCurrent(entry.snapshot)) throw new Error('Profile photo unavailable.')
        entry.snapshot = capturePhotoSession()
        const response = await axios.get(source, { responseType: 'blob', timeout: 15000,
          signal: entry.controller.signal, headers: { Authorization: `Bearer ${entry.snapshot.token}` } })
        if (entry.stale || !isPhotoSessionCurrent(entry.snapshot, response)) throw new Error('Profile photo unavailable.')
        const blob = response.data
        const contentType = (blob?.type || response.headers?.['content-type'] || '').split(';')[0].toLowerCase()
        if (!(blob instanceof Blob) || !['image/png', 'image/jpeg'].includes(contentType)
          || !blob.size || blob.size > MAX_PHOTO_BYTES) throw new Error('Profile photo unavailable.')
        entry.bytes = blob.size
        entry.url = URL.createObjectURL(blob)
        return entry.url
      } catch {
        discard(entry)
        throw new Error('Profile photo unavailable.')
      }
    })()
  }
  entry.references += 1
  entry.usedAt = Date.now()
  let released = false
  return { source, promise: entry.promise, release: () => {
    if (released) return
    released = true
    entry.references -= 1
    entry.usedAt = Date.now()
    if (!entry.references && !entry.url) discard(entry)
    trimIdleCache()
  } }
}

export function useProfilePhoto(sourceGetter) {
  capturePhotoSession()
  const src = ref('')
  const loading = ref(false)
  const failed = ref(false)
  let lease
  let sequence = 0
  const source = () => typeof sourceGetter === 'function' ? sourceGetter() : sourceGetter.value
  const isPrivate = computed(() => isPrivateProfilePhotoUrl(source()))
  const load = async () => {
    const current = ++sequence
    lease?.release()
    src.value = ''
    failed.value = false
    const nextSource = source()
    if (!isPrivateProfilePhotoUrl(nextSource)) {
      src.value = publicSource(nextSource)
      loading.value = false
      return
    }
    loading.value = true
    lease = acquireProfilePhoto(nextSource)
    try {
      const url = await lease.promise
      if (current === sequence) src.value = url
    } catch { if (current === sequence) failed.value = true }
    finally { if (current === sequence) loading.value = false }
  }
  watch([source, epoch], load, { immediate: true })
  onBeforeUnmount(() => { sequence += 1; lease?.release() })
  return { src: readonly(src), loading: readonly(loading), failed: readonly(failed), isPrivate }
}

const decodeImage = image => new Promise((resolve, reject) => {
  let timer
  const done = async () => {
    clearTimeout(timer)
    image.removeEventListener('load', done)
    image.removeEventListener('error', fail)
    try {
      if (!image.naturalWidth) throw new Error('Profile photo unavailable.')
      if (typeof image.decode === 'function') await image.decode()
      resolve()
    } catch { reject(new Error('Profile photo unavailable.')) }
  }
  const fail = () => {
    clearTimeout(timer)
    image.removeEventListener('load', done)
    image.removeEventListener('error', fail)
    reject(new Error('Profile photo unavailable.'))
  }
  if (image.complete) { done(); return }
  image.addEventListener('load', done, { once: true })
  image.addEventListener('error', fail, { once: true })
  timer = setTimeout(fail, 15000)
})

export async function awaitProfilePhotos(root) {
  const snapshot = capturePhotoSession()
  await nextTick()
  const selector = '[data-profile-photo], [data-private-profile-photo]'
  const nodes = [...(root?.matches?.(selector) ? [root] : []),
    ...Array.from(root?.querySelectorAll?.(selector) || [])]
  const leases = [...new Set(nodes.map(node => node.getAttribute('data-private-profile-photo')))]
    .filter(isPrivateProfilePhotoUrl).map(acquireProfilePhoto)
  const release = () => leases.forEach(lease => lease.release())
  try {
    await Promise.all(leases.map(lease => lease.promise))
    await nextTick()
    if (leases.length && !isPhotoSessionCurrent(snapshot)) throw new Error('Profile photo unavailable.')
    const images = nodes.flatMap(node => {
      const photos = node.matches('img') ? [node] : Array.from(node.querySelectorAll('img'))
      if (!photos.length) throw new Error('Profile photo unavailable.')
      return photos
    })
    await Promise.all(images.map(decodeImage))
    if (leases.length && !isPhotoSessionCurrent(snapshot)) throw new Error('Profile photo unavailable.')
    return release
  } catch (error) { release(); throw error }
}
